// @flow

import * as React from 'react';
import * as Prism from 'prismjs';
import classNames from 'classnames';

type InteractiveTokenPlaceholder = {|
  element: React.Element<any>,
  marker: string,
  value: string,
|};

type RenderableChunk =
  | string
  | {type: string, content: string | Array<RenderableChunk>};

type CodeRenderResult = {|
  code: React.Node,
  plaintext: string,
  count: number,
|};

const PLACEHOLDER_REGEXP = /(['"]?)(__TOKEN_PLACEHOLDER_\d+__)\1/;

const renderString = (
  input: string,
  path: string,
  placeholders: Array<InteractiveTokenPlaceholder>,
) => {
  // Check to see if the string contains any interactive token placeholders.
  // If not, we can just use the string verbatim.
  const match = PLACEHOLDER_REGEXP.exec(input);
  if (!match) {
    return input;
  }

  const content = match[0];
  const marker = match[2];

  // There should always be a corresponding placeholder, unless somehow
  // the input string contains a literal value that matches the regex.
  // That shouldn't happen. :)
  const placeholder = placeholders.find((p) => p.marker === marker);
  if (!placeholder) {
    return input;
  }

  // Carve out the piece of the content that matches the placeholder's marker.
  const preamble = input.substr(0, match.index);
  const postamble = input.substr(match.index + content.length);

  // Replace the placeholder's marker with the actual value represented
  // by the interactive token, and pass it along to the React component.
  // This is necessary since the string content represented by the token
  // may include quotes surrounding the value.
  const element = React.cloneElement(
    placeholder.element,
    {key: `token.${path}.interactive`},
    content.replace(marker, placeholder.value),
  );

  return [preamble, element, postamble];
};

const renderChunk = (
  chunk: RenderableChunk,
  path: string,
  placeholders: Array<InteractiveTokenPlaceholder>,
): React.Node => {
  if (typeof chunk === 'string') {
    return renderString(chunk, path, placeholders);
  }

  let content;
  if (Array.isArray(chunk.content)) {
    content = chunk.content.map((item, index) =>
      renderChunk(item, `${path}.${index}`, placeholders),
    );
  } else {
    content = renderString(chunk.content, path, placeholders);
  }

  return (
    <span key={`token.${path}`} className={classNames('token', chunk.type)}>
      {content}
    </span>
  );
};

/**
 * Transforms the content of a Markdoc code fence into a (possibly syntax-highlighted)
 * form which can contain interactive elements.
 */
export const renderCode = (
  children: React.Node,
  language?: string,
  visibleCount?: number,
): CodeRenderResult => {
  let input = '';
  let plaintext = '';
  const placeholders: Array<InteractiveTokenPlaceholder> = [];

  if (typeof children === 'string') {
    // If we were passed a raw string, it wasn't preprocessed by Markdoc,
    // so we don't need to worry about handling interactive placeholders.
    input = children;
    plaintext = children;
  } else {
    React.Children.forEach(children, (element) => {
      if (!element) {
        // Ignore children which are null or undefined.
      } else if (typeof element !== 'object') {
        // If the element is a raw scalar (number, string), it was not
        // preprocessed by Markdoc, so we dont need to worry about interactive placeholders.
        // Note: React.Children.forEach will filter out booleans
        input += String(element);
        plaintext += String(element);
      } else {
        // If the element is a React component, insert a placeholder into the
        // content we'll pass to Prism. After tokenization, this marker will
        // exist within the syntax-highlighted markup, and we can use it to
        // re-insert the actual React component.
        const marker = `__TOKEN_PLACEHOLDER_${placeholders.length}__`;
        const value = element.props.value;
        input += marker;
        plaintext += value;
        placeholders.push({element, marker, value});
      }
    });
  }

  // Remove whitespace surrounding the code, which can throw off line number
  // counts. In particular, this happens when using {% highlight %} elements
  // at the beginning or end of code blocks.
  input = input.trim();

  const grammar = Prism.languages[language];
  const count = input ? input.split('\n').length : 0;

  if (visibleCount) {
    // Truncate code input to only show the visible lines
    input = input
      .split('\n')
      .slice(0, visibleCount)
      .join('\n');
  }

  let code;

  if (!grammar) {
    // If there's no Prism grammar, just use the plaintext version.
    code = plaintext;
  } else {
    // Use Prism to tokenize the input, and render each individual chunk
    // to allow for insertion of interactive tokens.
    code = Prism.tokenize(input, grammar).map((token, index) =>
      renderChunk(token, index.toString(), placeholders),
    );
  }

  return {code, plaintext, count};
};
